import React, { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { FiCalendar, FiMail, FiPhone } from "react-icons/fi";
import { Tooltip } from "antd";
import { RootState } from "../../store";
import "./SelectedClientCard.css";

export type SelectedClientCardDensity = "auto" | "full" | "compact";

interface SelectedClientCardProps {
  density?: SelectedClientCardDensity;
  className?: string;
}

const getClientStatus = (value: number | string | undefined) => {
  switch (value) {
    case 0:
    case "pending":
      return { text: "Pending", tone: "pending" };
    case 1:
    case "active":
      return { text: "Active", tone: "active" };
    case 2:
    case "deactive":
      return { text: "Deactivated", tone: "inactive" };
    case 3:
    case "Expired":
      return { text: "Expired", tone: "expired" };
    default:
      return { text: "Unknown", tone: "unknown" };
  }
};

const formatClientSince = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const SelectedClientCard: React.FC<SelectedClientCardProps> = ({
  density = "auto",
  className = "",
}) => {
  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient.selectedClient,
  );

  const nameRef = useRef<HTMLElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);

  const checkNameTruncation = useCallback(() => {
    const el = nameRef.current;
    if (el) setIsNameTruncated(el.scrollWidth > el.offsetWidth);
  }, []);

  useEffect(() => {
    checkNameTruncation();
    const observer = new ResizeObserver(checkNameTruncation);
    if (nameRef.current) observer.observe(nameRef.current);
    return () => observer.disconnect();
  }, [checkNameTruncation, selectedClient]);

  if (!selectedClient) return null;

  const status = getClientStatus(selectedClient.status);
  const name = selectedClient.name || "Unnamed client";
  const initial = name.trim()?.[0]?.toUpperCase() || "C";
  const phone = selectedClient.phoneNumber || selectedClient.phone;
  const email = selectedClient.email;
  const clientSince = formatClientSince(
    selectedClient.clientSince ??
      selectedClient.createdAt ??
      selectedClient.createdOn ??
      selectedClient.createdDate,
  );

  return (
    <section
      className={`selected-client-card selected-client-card--${density} ${className}`.trim()}
      aria-label="Selected client"
      tabIndex={0}
    >
      <div className="selected-client-card__bar">
        <div className="selected-client-card__avatar" aria-hidden="true">
          {initial}
        </div>
        <span className="selected-client-card__label">Selected client</span>

        <Tooltip title={isNameTruncated ? name : ""} placement="bottom">
          <strong
            ref={nameRef as React.Ref<HTMLElement>}
            className="selected-client-card__name"
          >
            {name}
          </strong>
        </Tooltip>


       
        <span
          className={`selected-client-card__status selected-client-card__status--${status.tone}`}
        >
          <span className="selected-client-card__status-dot" aria-hidden="true" />
          <span className="selected-client-card__meta-text">{status.text}</span>
        </span>

        {email && (
          <Tooltip title={email} placement="bottom">
            <span className="selected-client-card__meta selected-client-card__email">
              <FiMail className="selected-client-card__icon" />
              <span className="selected-client-card__meta-text">{email}</span>
            </span>
          </Tooltip>
        )}

        {phone && (
          <Tooltip title={phone} placement="bottom">
            <span className="selected-client-card__meta selected-client-card__phone">
              <FiPhone className="selected-client-card__icon" />
              <span className="selected-client-card__meta-text">{phone}</span>
            </span>
          </Tooltip>
        )}

        {clientSince && (
          <Tooltip title={`Client Since: ${clientSince}`} placement="bottom">
            <span className="selected-client-card__meta selected-client-card__since">
              <FiCalendar className="selected-client-card__icon" />
              <span className="selected-client-card__meta-text">Client Since: {clientSince}</span>
            </span>
          </Tooltip>
        )}

        <span
          className={`selected-client-card__dot selected-client-card__dot--${status.tone}`}
          aria-label={status.text}
        />
      </div>
    </section>
  );
};

export default SelectedClientCard;
